import clsx from 'clsx';
import { Ref, forwardRef, useId, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ButtonProps,
  IS_BUTTON_SYMBOL,
} from 'src/components/common/Button/tools';
import './Button.scss';

function TheButton(
  {
    autoFocus = false,
    color = 'default',
    type: typeProps,

    className,
    disabled,
    isActive,
    allowInteractionWhenDisabled = false,
    inputId: propsInputId,
    linkTo,
    rounding: shape = 'rounded',
    children,

    insetFocus,

    touchingTop,
    touchingBottom,
    touchingLeft,
    touchingRight,

    onClick,
    onDoubleClick,
    onFocus,
    onMouseEnter,
    onMouseLeave,

    ...htmlButtonProps
  }: ButtonProps,
  ref: Ref<HTMLSpanElement | HTMLAnchorElement | HTMLButtonElement>,
) {
  const id = useId();
  const inputId = propsInputId ?? id;
  const type = (!typeProps && !!linkTo ? 'link' : typeProps) ?? 'button';

  const handleClick = (event: MouseEvent) => {
    if (!onClick || (disabled && !allowInteractionWhenDisabled)) return;

    onClick({ originalEvent: event });
  };

  const handleDoubleClick = (event: MouseEvent) => {
    if (!onDoubleClick || (disabled && !allowInteractionWhenDisabled)) return;

    onDoubleClick({ originalEvent: event });
  };

  const sharedButtonProps = {
    id: inputId,
    className: 'the-button__button',
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    onFocus: onFocus,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  return (
    <span
      className={clsx([
        className,
        'the-button',
        `the-button--color-${color}`,
        `the-button--type-${linkTo ? 'link' : type}`,
        `the-button--shape-${shape}`,
        {
          'the-button--inset-focus': insetFocus,
          'the-button--is-disabled': disabled,
          'the-button--is-active': isActive,
          'the-button--has-link': type === 'link' && linkTo,
          'the-button--has-action':
            onClick || onDoubleClick || type === 'submit',
          'the-button--touch-top': touchingTop,
          'the-button--touch-bottom': touchingBottom,
          'the-button--touch-left': touchingLeft,
          'the-button--touch-right': touchingRight,
        },
      ])}
    >
      {type === 'link' ?
        <Link
          ref={ref as Ref<HTMLAnchorElement>}
          {...sharedButtonProps}
          {...htmlButtonProps}
          to={linkTo ?? ''}
          aria-selected={isActive}
        >
          {children}
        </Link>
      : type === 'tab' ?
        <span
          ref={ref as Ref<HTMLSpanElement>}
          {...sharedButtonProps}
          aria-selected={isActive}
          role="tab"
        >
          {children}
        </span>
      : onClick || onDoubleClick || type === 'submit' ?
        <button
          ref={ref as Ref<HTMLButtonElement>}
          {...sharedButtonProps}
          {...htmlButtonProps}
          autoFocus={autoFocus}
          disabled={disabled && !allowInteractionWhenDisabled}
          aria-disabled={disabled}
          type={
            type === 'button' ? 'button'
            : type === 'submit' ?
              'submit'
            : 'reset'
          }
        >
          {children}
        </button>
      : <span ref={ref} {...sharedButtonProps}>
          {children}
        </span>
      }
    </span>
  );
}

TheButton.symbol = IS_BUTTON_SYMBOL;

export default forwardRef(TheButton);
